import { Capacitor } from '@capacitor/core';
import type { AppEnvironment, RuntimeDevConfig } from './types';
import { env as baseEnv } from '@mode-env';

export type {
  AppEnvironment,
  MemberPortalAppShell,
  AuthMode,
  PageHeroCopy,
  ResolvedPageHeroCopy,
  ResidentAppPageCopy,
} from './types';
export type { ResolvedPageCopy } from './page-copy-defaults';

declare const __GIT_COMMIT_SHA__: string;
declare const __APP_VERSION__: string;

declare global {
  interface Window {
    __IP4X_DEV__?: RuntimeDevConfig;
  }
}

function readRuntimeApiOverrides(): RuntimeDevConfig {
  if (typeof window === 'undefined') return {};
  return window.__IP4X_DEV__ ?? {};
}

function rewriteLocalhostForNative(url: string): string {
  if (!url || !Capacitor.isNativePlatform()) return url;

  const parsed = new URL(url);
  if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
    return url;
  }

  if (Capacitor.getPlatform() === 'android') {
    parsed.hostname = '10.0.2.2';
  }

  return parsed.toString();
}

const runtimeOverrides = readRuntimeApiOverrides();
const apiBaseUrl = rewriteLocalhostForNative(runtimeOverrides.apiBaseUrl?.trim() || baseEnv.apiBaseUrl);
const coreApiUrl = rewriteLocalhostForNative(runtimeOverrides.coreApiUrl?.trim() || baseEnv.coreApiUrl);

/**
 * Resolve the build's app-template pin. This is OPTIONAL: when a build does not
 * pin a template (empty value) the app must still boot and load the default
 * domain's branding/config — the backend then resolves the tenant's effective
 * member template on its own. A hard throw here ran at module-import time, before
 * any domain resolution, and white-screened every build with an empty value
 * (losing the base branding load). We therefore only *warn* and fall back to ''
 * rather than crashing; a malformed (non-empty) value is ignored the same way.
 */
function normalizeAppTemplateId(value: string, buildMode: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidPattern.test(normalized)) {
    console.warn(`[env] Ignoring invalid appTemplateId for resident-app build "${buildMode}": "${normalized}"`);
    return '';
  }

  return normalized;
}

export const env: AppEnvironment = {
  ...baseEnv,
  version: (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) || baseEnv.version,
  apiBaseUrl,
  coreApiUrl,
  appTemplateId: normalizeAppTemplateId(baseEnv.appTemplateId, baseEnv.buildMode),
  gitCommitSha:
    typeof __GIT_COMMIT_SHA__ !== 'undefined' && __GIT_COMMIT_SHA__
      ? __GIT_COMMIT_SHA__
      : undefined,
};
